import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useBienContext } from '../BienContext'

import { PlusIcon } from '@heroicons/react/24/outline'
import { EyeIcon } from '@heroicons/react/24/solid'
import { Input, Spinner, Toggle } from '@/components/ui'
import DetailTemplate from '@/components/DetailTemplate'
import { formatPrice } from '@/utils/formatters'
import { capitalize } from '@/utils/strings'

import { emptyLocation, setCurrentLocation,
  setNewLocation,
  locationTypes,
  setLocationInEdition, resetLocationInEdition, 
  isRegisteredLocation} from '../../locations/locationsSlice'
import { setBien as setQuittancesBien, setLocation as setQuittancesLocation } from "../../quittances/quittancesSlice"

import EditButtons from '../EditButtons'
import Form from '../../locations/Form'
import { FORM_TYPES } from '../../locations/LocationFormContext'
import LocationQuittances from './LocationQuittances'

export default () => {
  const dispatch = useDispatch()
  const { bien, loading, markSectionAsUpdated, updatedSections, isPendingAction } = useBienContext()
  const location = useSelector(state => state.locations.current || emptyLocation)

  const [showQuittances, setShowQuittances] = useState(false)

  const SECTION_ID = 'location'
  const SECTION_TITLE = 'Location'

  useEffect(
    () => {
      if(!location.id && bien.id && bien.current_location) {
        dispatch(setCurrentLocation(bien.current_location))
      }
    },
    [ bien.id, bien.current_location ]
  )

  // État local pour suivre le type de formulaire à afficher
  const [formType, setFormType] = useState(FORM_TYPES.EDIT)

  // Composant de formulaire avec le contexte
  const modalContent = () => showQuittances ?
  <LocationQuittances /> : 
  <Form formType={formType} />

  const AddNewLocationButton = ({onEditClick}) => {
    const editLocation = !isRegisteredLocation(location) ? location : setNewLocation(location)
    const handleClick = async (event) => {
      event.preventDefault()
      event.stopPropagation()
      await dispatch(setLocationInEdition(editLocation))
      setFormType(FORM_TYPES.NEW) // Définir le type de formulaire sur NEW
      onEditClick()
    }

    return (
    <div>
      <button 
        className="btn btn-sm btn-primary"
        title="Créer une location"
        onClick={handleClick}
      >
        <PlusIcon className="h-5 w-5 mr-1"/> Arrivée locataire
      </button>
    </div>
  )}

  const handleQuittancesClick = () => {
    if(bien && bien.current_location) {
      dispatch(setQuittancesBien(bien))
      dispatch(setQuittancesLocation(bien.current_location))
      setShowQuittances(true)
    }
  }

  const LocationButtons = ({onEditClick}) => {
    const editLocation = location.date_fin_bail ? setNewLocation(location) : location
    return (
      <div className="flex space-x-4">
        { (location.date_effet_bail && !location.date_fin_bail) && 
          <button 
            id='quittancesBtn' 
            className={`btn btn-sm btn-ghost hover:bg-primary-400 ${
              isPendingAction ? 'loading' : ''
            }`} 
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              if (!isPendingAction) {
                handleQuittancesClick()
                onEditClick()
              }
            }}
            disabled={isPendingAction}
            title="Voir les quittances"
          >
            <EyeIcon className="h-5 w-5"/> Quittances
          </button>
        }
        { (!isRegisteredLocation(location) || location.date_fin_bail) && <AddNewLocationButton onEditClick={onEditClick} /> }
        <EditButtons
          bienId={bien.id}
          isArchived={!!bien.archived_at}
          isPendingAction={isPendingAction}
          title="Modifier la location"
          onEditClick={async () => {
            await dispatch(setLocationInEdition(editLocation))
            setFormType(FORM_TYPES.EDIT) // Définir le type de formulaire sur EDIT
            onEditClick()
          }}
        />  
      </div>
    )
  }

  const onCloseModal = () => {
    dispatch(resetLocationInEdition())
    // Réinitialiser le type de formulaire à EDIT par défaut
    setFormType(FORM_TYPES.EDIT)
    // Réinitialiser l'état des quittances
    setShowQuittances(false)
  }

  const chargesCss = "flex flex-wrap items-center lg:w-1/2"
  const chargesValuesCss = "flex-grow text-end"

  return(
    <DetailTemplate
      title={SECTION_TITLE}
      sectionId={SECTION_ID}
      bodyProps={{columns: 3}}
      updatedSections={updatedSections}
      loading={false}
      isPendingAction={isPendingAction}
      editButton={<LocationButtons />}
      modalContent={modalContent()}
      onUpdateSuccess={markSectionAsUpdated}
      onCloseModalCallback={onCloseModal}
      showBackdrop={showQuittances}
      showCloseButton={showQuittances}
    >
      { loading && 
        <div className="space-y-4 col-span-3">
          <Spinner color='primary'/>
        </div>
      }
      {!loading && (
        <>
          <div className="space-y-4">
            <Input.Display label="Loyer HC" value={formatPrice(location.prix_loyer)} className={chargesCss} valueClassName={chargesValuesCss}/>

            {/* Charges */}
            <div className="space-y-2">
              <Input.Display label="Syndic" value={formatPrice(location.montant_charges_syndic)} className={chargesCss} valueClassName={chargesValuesCss} />
              <Input.Display label="Eau Froide" value={formatPrice(location.montant_charges_eau_froide)} className={chargesCss} valueClassName={chargesValuesCss}/>
              <Input.Display label="Ménage communs" value={formatPrice(location.montant_charges_menage_commun)} className={chargesCss} valueClassName={chargesValuesCss}/>
              <Input.Display label="Electricité communs" value={formatPrice(location.montant_charges_electricite_commun)} className={chargesCss} valueClassName={chargesValuesCss}/>
              <Input.Display label="Ordures ménagères" value={formatPrice(location.montant_charges_ordures_menageres)} className={chargesCss} valueClassName={chargesValuesCss}/>
              <Input.Display label="Entretien chauffage" value={formatPrice(location.montant_charges_entretien_chauffage)} className={chargesCss} valueClassName={chargesValuesCss}/>
              <Input.Display label="Espaces verts" value={formatPrice(location.montant_charges_espaces_verts)} className={chargesCss} valueClassName={chargesValuesCss}/>
            </div>
            <div className="divider my-1"></div>
            <Input.Display label="Charges totales" value={formatPrice(location.montant_charges_total)} className={chargesCss} valueClassName={chargesValuesCss}/>
          </div>
          <div className="space-y-4">
            <Input.Display label="Type de Location" value={capitalize(locationTypes[location.type_location] || location.type_location)} />
            <Input.Display label="Loyer CC" value={formatPrice(location.prix_loyer_charges_comprises)} />
            <Input.Display label="Honoraires TTC" value={formatPrice(location.montant_honoraires_ttc)} />
            <Input.Display label="Montant GLI" value={formatPrice(location.montant_gli)} />
            <Input.Display label="Net propriétaire" value={formatPrice(location.prix_net_proprietaire)} /> 
          </div>
          <div>
            <Input.Display label="Dépôt de garantie" value={formatPrice(location.montant_depot_garantie)} />
            <Toggle.Display label="Dépôt garantie conservé à l'agence" value={location.depot_garantie_conserve_agence}/>
            <Input.Display label="Frais d'entrée" value={formatPrice(location.montant_frais_entree)} />
          </div>
        </>
      )}
    </DetailTemplate>
  )
}